import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  CircularProgress,
  Input,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

// Import custom hooks
import { useGetAllDirections } from "../../hooks/query/useDirection";
import { useGetAllRooms } from "../../hooks/query/useRoom";
import { useGetAllColors } from "../../hooks/query/useColor";
import {
  useCheckVastuRemedy,
  useGetVastuSelection,
} from "../../hooks/query/useVastu";
import ClearIcon from "@mui/icons-material/Clear";

import { DirectionRequest, VastuRequest } from "../../types/VastuRemedy";

import * as toast from "../../utils/alertMessage";
import DocumentUploadDialog from "../../components/property/DocumentUploadDialog";
import { UploadFile } from "@mui/icons-material";

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#bb0000",
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: "#990000",
  },
  padding: theme.spacing(1.5, 3),
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  width: "100%",
  marginTop: theme.spacing(2),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontWeight: 600,
  fontSize: "0.875rem",
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ResponsiveChipContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  maxHeight: 100,
  overflowY: "auto",
}));

const VastuAnalysisTablePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const { propertyId } = useParams();
  if (!propertyId) {
    throw new Error("Property Not Found");
  }
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { control, handleSubmit, setValue } = useForm();
  const {
    data: directionsData,
    isLoading: isDirectionsLoading,
    error: directionsError,
  } = useGetAllDirections();

  const {
    data: colorsData,
    isLoading: isColorsLoading,
    error: colorsError,
  } = useGetAllColors();

  const {
    data: roomsData,
    isLoading: isRoomsLoading,
    error: roomsError,
  } = useGetAllRooms();

  const { mutateAsync: checkVastuRemedy, isLoading: isSubmitting } =
    useCheckVastuRemedy();

  const { data: previousFormData, isLoading: isPreviousFormDataLoading } =
    useGetVastuSelection(propertyId);

  console.log("PREVVV", previousFormData);
  console.log(isPreviousFormDataLoading);

  if (previousFormData && previousFormData.length > 0) {
    console.log(previousFormData);
    previousFormData.forEach((directionData: any) => {
      const direction = directionData.direction;

      // Set form values for each attribute
      setValue(`entrances_${direction}`, directionData.entrances || []);
      setValue(`rooms_${direction}`, directionData.rooms || []);
      setValue(`toiletseat_${direction}`, directionData.toiletSeat || []);
      setValue(
        `kitchenburnerslab_${direction}`,
        directionData.kitchenBurnerSlab || []
      );
      setValue(`areaSqFt_${direction}`, directionData.areaSqFt || "");
      setValue(`wallcolor_${direction}`, directionData.wallColor || []);
      setValue(`floorcolor_${direction}`, directionData.floorColor || []);
    });
  }

  // Loading and error handling
  if (
    isDirectionsLoading ||
    isColorsLoading ||
    isRoomsLoading ||
    isSubmitting ||
    isPreviousFormDataLoading
  ) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (directionsError || colorsError || roomsError) {
    return (
      <Box p={4}>
        <Typography variant="h6" color="error">
          Failed to load data.
        </Typography>
      </Box>
    );
  }

  // Data preparation
  const directions = directionsData || [];
  const colors = colorsData || [];
  const rooms = roomsData || [];

  // Attributes for the table
  const attributes = [
    "Entrances",
    // "Color",
    // "Object",
    "Toilet Seat",
    "Kitchen Burner Slab",
    "Rooms",
    "Wall Color",
    "Floor Color",
    "Area (sq ft)",
  ];

  const getDisplayNameById = (list: any[], id: string) => {
    const item = list.find((element) => element.id === id);
    return item ? item.displayName : id;
  };

  const onSubmit = async (formData: any) => {
    console.log("FormData:::", formData);
    const formattedData: VastuRequest = directions
      .map((direction: any) => {
        const directionData: DirectionRequest = {
          direction: direction.id,
          entrances: formData[`entrances_${direction.id}`] || [],
          // object: formData[`object_${direction.id}`] || [],
          toiletSeat: formData[`toiletseat_${direction.id}`] || [],
          kitchenBurnerSlab:
            formData[`kitchenburnerslab_${direction.id}`] || [],
          rooms: formData[`rooms_${direction.id}`] || [],
          areaSqFt: formData[`areaSqFt_${direction.id}`]
            ? parseFloat(formData[`areaSqFt_${direction.id}`])
            : null,
          objects: formData[`objects_${direction.id}`] || [],
          wallColor: formData[`wallcolor_${direction.id}`] || [],
          floorColor: formData[`floorcolor_${direction.id}`] || [],
        };

        // Corrected filtering logic
        const filteredData = Object.fromEntries(
          Object.entries(directionData).filter(
            ([, value]) =>
              value !== null &&
              value !== undefined &&
              value !== "" &&
              !(Array.isArray(value) && value.length === 0)
          )
        );

        return filteredData as DirectionRequest;
      })
      .filter((data) => Object.keys(data).length > 1);

    console.log(formattedData);

    await checkVastuRemedy(
      { requestBody: formattedData, propertyId: propertyId },
      {
        onSuccess: (response) => {
          toast.success("Vastu remedy check completed successfully");
          navigate(`/remedy/${response?.data?.id}`);
        },
        onError: (error: any) => {
          toast.error("Failed to check Vastu remedy");
          console.error("Error:", error);
        },
      }
    );
  };
  // const { mutateAsync: postVastuHelp } = usePostVastuHelp();

  const handleDocumentDialogSubmit = async (data: any) => {
    console.log("Submitted Data:", data);

    // await postVastuHelp({ propertyId, formData: data });

    setDocumentDialogOpen(false);
  };

  const renderCellContent = (attribute: string, direction: any) => {
    const attributeKeyMap: { [key: string]: string } = {
      Entrances: `entrances_${direction.id}`,
      Rooms: `rooms_${direction.id}`,
      "Toilet Seat": `toiletseat_${direction.id}`,
      "Kitchen Burner Slab": `kitchenburnerslab_${direction.id}`,
      "Wall Color": `wallcolor_${direction.id}`,
      "Floor Color": `floorcolor_${direction.id}`,
      "Area (sq ft)": `areaSqFt_${direction.id}`,
    };

    const previousData =
      previousFormData &&
      previousFormData.formData.find(
        (data: any) => data.direction === direction.id
      );
    console.log(previousData);

    // Determine the default value based on the attribute and previous data
    const defaultValue = (() => {
      if (!previousData) return attribute === "Area (sq ft)" ? "" : [];

      const key = attributeKeyMap[attribute];
      if (!key) return attribute === "Area (sq ft)" ? "" : [];

      // Special handling for area
      if (attribute === "Area (sq ft)") {
        return previousData.areaSqFt ? previousData.areaSqFt.toString() : "";
      }

      // Handle other attributes
      switch (attribute) {
        case "Entrances":
          return previousData.entrances || [];
        case "Rooms":
          return previousData.rooms || [];
        case "Toilet Seat":
          return previousData.toiletSeat || [];
        case "Kitchen Burner Slab":
          return previousData.kitchenBurnerSlab || [];
        case "Wall Color":
          return previousData.wallColor || [];
        case "Floor Color":
          return previousData.floorColor || [];
        default:
          return attribute === "Area (sq ft)" ? "" : [];
      }
    })();
    if (attribute === "Area (sq ft)") {
      return (
        <Controller
          name={`areaSqFt_${direction.id}`}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              fullWidth
              disableUnderline
              sx={{
                backgroundColor: theme.palette.background.default,
                padding: theme.spacing(1),
                borderRadius: 2,
                fontSize: "0.875rem",
              }}
              placeholder="Area"
            />
          )}
        />
      );
    }

    // if (attribute === "Toilet Seat" || attribute === "Kitchen Burner Slab") {
    //   return (
    //     <Controller
    //       name={attributeKeyMap[attribute]}
    //       control={control}
    //       defaultValue={defaultValue}
    //       render={({ field }) => (
    //         <FormControl fullWidth>
    //           <Select
    //             {...field}
    //             value={field.value || ""}
    //             input={<OutlinedInput />}
    //             renderValue={(selected) => {
    //               if (!selected) return "";
    //               const displayName = getDisplayNameById(
    //                 colors,
    //                 selected as string
    //               );
    //               return (
    //                 <Chip
    //                   label={displayName}
    //                   size="small"
    //                   sx={{
    //                     backgroundColor: "#bb0000",
    //                     color: "white",
    //                     fontSize: "0.675rem",
    //                   }}
    //                 />
    //               );
    //             }}
    //             sx={{
    //               "& .MuiSelect-select": {
    //                 padding: theme.spacing(1),
    //                 fontSize: "0.875rem",
    //               },
    //             }}
    //           >
    //             <MenuItem value="">
    //               <em>None</em>
    //             </MenuItem>
    //             {colors.map((color) => (
    //               <MenuItem key={color.id} value={color.id}>
    //                 {color.displayName}
    //               </MenuItem>
    //             ))}
    //           </Select>
    //         </FormControl>
    //       )}
    //     />
    //   );
    // }

    return (
      <Controller
        name={`${attribute.toLowerCase().replace(/\s+/g, "")}_${direction.id}`}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => {
          const showClearIcon =
            Array.isArray(field.value) && field.value.length > 0;

          return (
            <FormControl fullWidth>
              <Select
                {...field}
                multiple={true}
                endAdornment={
                  showClearIcon ? (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange([]);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <ResponsiveChipContainer>
                    {(Array.isArray(selected)
                      ? selected.slice(0, 3)
                      : [selected]
                    ).map((value) => {
                      const displayName = getDisplayNameById(
                        attribute === "Rooms" ? rooms : colors,
                        value
                      );
                      return (
                        <Chip
                          key={value}
                          label={displayName}
                          size="small"
                          sx={{
                            backgroundColor: "#bb0000",
                            color: "white",
                            fontSize: "0.675rem",
                          }}
                        />
                      );
                    })}
                    {Array.isArray(selected) && selected.length > 3 && (
                      <Chip
                        label={`+${selected.length - 3}`}
                        size="small"
                        onDelete={() => {
                          console.log("clicked");
                        }}
                        sx={{
                          backgroundColor: theme.palette.grey[300],
                          fontSize: "0.675rem",
                        }}
                      />
                    )}
                  </ResponsiveChipContainer>
                )}
                sx={{
                  "& .MuiSelect-select": {
                    padding: theme.spacing(1),
                    fontSize: "0.875rem",
                  },
                }}
              >
                {attribute === "Entrances" &&
                  direction.entrances.map((entrance: any) => (
                    <MenuItem
                      key={entrance}
                      value={entrance}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {entrance}
                    </MenuItem>
                  ))}

                {attribute === "Rooms" &&
                  rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.displayName}
                    </MenuItem>
                  ))}

                {attribute === "Toilet Seat" &&
                  colors.map((seat) => (
                    <MenuItem key={seat.id} value={seat.id}>
                      {seat.displayName}
                    </MenuItem>
                  ))}

                {attribute === "Kitchen Burner Slab" &&
                  colors.map((color) => (
                    <MenuItem key={color.id} value={color.id}>
                      {color.displayName}
                    </MenuItem>
                  ))}

                {attribute === "Wall Color" &&
                  colors.map((color) => (
                    <MenuItem key={color.id} value={color.id}>
                      {color.displayName}
                    </MenuItem>
                  ))}

                {attribute === "Floor Color" &&
                  colors.map((color) => (
                    <MenuItem key={color.id} value={color.id}>
                      {color.displayName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          );
        }}
      />
    );
  };

  const renderContent = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            maxWidth: { xs: "95vw", sm: "90vw", md: "95vw", lg: "1200px" },
            mx: "auto",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: { xs: "70vh", sm: "75vh", md: "80vh", lg: "85vh" },
            "& th": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#bb0000",
              color: "white",
            },
          }}
        >
          <Table
            sx={{
              minWidth: 800,
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: "#bb0000",
                    color: "white",
                  }}
                >
                  Attribute
                </StyledTableCell>
                {directions.map((direction) => (
                  <StyledTableCell
                    key={direction.id}
                    align="center"
                    sx={{
                      position: "sticky",
                      backgroundColor: "#bb0000",
                      color: "white",
                      minWidth: 150,
                    }}
                  >
                    {direction.displayName}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {attributes.map((attribute) => (
                <TableRow key={attribute}>
                  <StyledTableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "white",
                      fontWeight: 600,
                    }}
                  >
                    {attribute}
                  </StyledTableCell>
                  {directions.map((direction) => (
                    <StyledTableCell key={direction.id}>
                      {renderCellContent(attribute, direction)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* )} */}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        p: isMobile ? 2 : 4,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            mb: 2,
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 700,
              color: "#bb0000",
              textAlign: isMobile ? "center" : "left",
              mb: isMobile ? 2 : 0,
            }}
          >
            Vastu Analysis
          </Typography>

          {isMobile ? (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <PrimaryButton variant="contained" type="submit">
                Generate Report
              </PrimaryButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                // justifyContent: "space-between",
                // alignItems: isMobile ? "stretch" : "center",
                mb: 2,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                // type="submit"
                sx={{
                  backgroundColor: "#bb0000",
                  "&:hover": { backgroundColor: "#990000" },
                }}
                startIcon={<UploadFile />}
                onClick={() => {
                  setDocumentDialogOpen(true);
                }}
              >
                Upload
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  backgroundColor: "#bb0000",
                  "&:hover": { backgroundColor: "#990000" },
                }}
              >
                Generate Report
              </Button>
            </Box>
          )}
        </Box>

        {renderContent()}
      </form>
      <DocumentUploadDialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        onSubmit={handleDocumentDialogSubmit}
        propertyId={propertyId}
      />
    </Box>
  );
};

export default VastuAnalysisTablePage;
